import { supabaseFetch } from "./supabaseClient";

// Canonical size order matching V2's sizes table sort_order
export const SIZE_ORDER = {
  EACH: 1, "2XS": 2, XS: 3, SM: 4, MED: 5, LG: 6, "TALL LG": 7, LT: 8,
  XL: 9, "TALL XL": 10, XLT: 11, "2XL": 12, "3XL": 13, "4XL": 14,
  "5XL": 15, "6XL": 16, "7XL": 17, "S/M": 18, "L/XL": 19, "XL+": 20,
};

function sortSizes(sizes) {
  if (!sizes || sizes.length <= 1) return sizes;
  return [...sizes].sort(
    (a, b) => (SIZE_ORDER[a.name] ?? 99) - (SIZE_ORDER[b.name] ?? 99)
  );
}

const PRODUCT_COLUMNS = [
  "display_id", "item_number", "stormtech_id", "name", "color", "gender",
  "is_clearance", "image_url", "image_url_override", "product_url",
  "primary_color_code", "secondary_color_code", "primary_color", "secondary_color",
  "price_cad", "price_usd", "price_gbp", "price_euro",
  "active_regions", "sizes", "current_inventory", "incoming_inventory", "updated_at"
].join(",");

// ─── Response Transformer ────────────────────────────────────────────

/**
 * Convert Supabase incoming_inventory (flat array) to V1 shape (grouped by warehouse).
 * Supabase: [{ warehouse, date, sizes: {...} }, ...]
 * V1:       { "NJ": [{ date, incomingBySize: {...} }], ... }
 */
function transformIncomingInventory(incoming) {
  if (!incoming || !Array.isArray(incoming)) return {};

  const grouped = {};
  for (const shipment of incoming) {
    const wh = shipment.warehouse;
    if (!wh) continue;
    if (!grouped[wh]) grouped[wh] = [];
    grouped[wh].push({
      date: shipment.date,
      type: shipment.type,
      has_container: shipment.has_container,
      incomingBySize: shipment.sizes || {},
    });
  }
  return grouped;
}

/**
 * Transform a single Supabase product row to V1's expected camelCase shape.
 */
function transformSupabaseProduct(row) {
  return {
    id: row.display_id,
    itemNumber: row.item_number,
    stormtechId: row.stormtech_id,
    name: row.name,
    color: row.color,
    gender: row.gender,
    isClearance: row.is_clearance,
    primaryColor: row.primary_color,
    secondaryColor: row.secondary_color,
    primaryColorCode: row.primary_color_code,
    secondaryColorCode: row.secondary_color_code,
    imageURL: row.image_url_override || row.image_url,
    productURL: row.product_url,
    activeRegions: row.active_regions || [],
    sizes: sortSizes(row.sizes || []),
    price: {
      CAD: row.price_cad,
      USD: row.price_usd,
      GBP: row.price_gbp,
      EURO: row.price_euro,
    },
    currentInventory: row.current_inventory || {},
    incomingInventory: transformIncomingInventory(row.incoming_inventory),
    updatedAt: row.updated_at ? new Date(row.updated_at).getTime() : null,
  };
}

/**
 * Group flat transformed products into V1's { itemNumber, colors } shape.
 * Does NOT apply color sorting — callers handle that with their existing sort logic.
 */
function groupByItemNumber(products) {
  const groups = {};
  for (const product of products) {
    if (!groups[product.itemNumber]) {
      groups[product.itemNumber] = [];
    }
    groups[product.itemNumber].push(product);
  }
  return Object.entries(groups).map(([itemNumber, colors]) => ({
    itemNumber,
    colors,
  }));
}

// ─── Public API ──────────────────────────────────────────────────────

/**
 * Autocomplete search for SearchBar.
 * Returns deduplicated results: one per unique item_number.
 */
export async function searchAutocomplete(query) {
  const data = await supabaseFetch("/rpc/search_products_autocomplete", {
    method: "POST",
    body: JSON.stringify({
      search_query: query,
      result_limit: 20,
      result_offset: 0,
    }),
  });

  // Deduplicate by item_number (keep first occurrence = highest rank)
  const seen = new Set();
  const deduplicated = [];
  for (const row of data) {
    if (!seen.has(row.item_number)) {
      seen.add(row.item_number);
      deduplicated.push(row);
    }
  }

  return deduplicated.map((row, index) => ({
    value: row.item_number,
    label: row.item_number,
    data: {
      itemNumber: row.item_number,
      itemName: row.name,
      isLastResult: index === deduplicated.length - 1,
    },
  }));
}

/**
 * Get all color/gender variants for a base item number.
 * Returns flat array of transformed products (caller groups and sorts).
 */
export async function getProductsByItemNumber(baseItemNumber) {
  const encoded = encodeURIComponent(baseItemNumber);
  const data = await supabaseFetch(
    `/products?select=${PRODUCT_COLUMNS}&item_number=like.${encoded}*&order=item_number.asc`
  );
  return data.map(transformSupabaseProduct);
}

/**
 * Get products by their display IDs (for project builder / public viewer).
 * Chunks at 150 IDs per request for URL length safety.
 * Returns flat array of transformed products (caller groups and sorts).
 */
export async function getProductsByDisplayIds(displayIds) {
  if (!displayIds || displayIds.length === 0) return [];

  const CHUNK_SIZE = 150;
  const chunks = [];
  for (let i = 0; i < displayIds.length; i += CHUNK_SIZE) {
    chunks.push(displayIds.slice(i, i + CHUNK_SIZE));
  }

  const results = await Promise.all(
    chunks.map((chunk) => {
      const idList = chunk.map(encodeURIComponent).join(",");
      return supabaseFetch(
        `/products?select=${PRODUCT_COLUMNS}&display_id=in.(${idList})`
      );
    })
  );

  return results.flat().map(transformSupabaseProduct);
}

/**
 * Batch-fetch minimal product metadata by UUID id (not display_id).
 * Used by admin surfaces like the stock-issues dashboard where rows
 * carry the raw products.id FK and need a human-readable label.
 * Returns a map keyed by UUID id.
 */
export async function getProductsByIds(ids) {
  if (!ids || ids.length === 0) return {};
  const CHUNK_SIZE = 150;
  const chunks = [];
  for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + CHUNK_SIZE));
  }
  const results = await Promise.all(
    chunks.map((chunk) => {
      const list = chunk.map(encodeURIComponent).join(",");
      return supabaseFetch(
        `/products?select=id,display_id,item_number,name&id=in.(${list})`
      );
    })
  );
  return Object.fromEntries(
    results.flat().map((r) => [r.id, r])
  );
}

/**
 * Batch-fetch full product detail by UUID id (not display_id). Returns a map
 * keyed by UUID containing the transformed product shape (image, current/
 * incoming inventory, pricing, sizes, etc.). Used by the stock-issues
 * dashboard rows and inventory dialog.
 *
 * Chunks at 150 IDs per request for URL length safety.
 */
export async function getProductsDetailById(ids) {
  if (!ids || ids.length === 0) return {};
  const CHUNK_SIZE = 150;
  const chunks = [];
  for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + CHUNK_SIZE));
  }
  const results = await Promise.all(
    chunks.map((chunk) => {
      const list = chunk.map(encodeURIComponent).join(",");
      // Include `id` (UUID) in the selected columns so we can key the map by
      // it — PRODUCT_COLUMNS does not include it by default.
      return supabaseFetch(
        `/products?select=id,${PRODUCT_COLUMNS}&id=in.(${list})`
      );
    })
  );
  const flat = results.flat();
  return Object.fromEntries(
    flat.map((row) => [row.id, transformSupabaseProduct(row)])
  );
}

// Export utilities for use by callers
export { groupByItemNumber, transformSupabaseProduct };
